// ============================================================================
// modules/parametros/hooks/useParametros.js
// Hook de caso de uso: orquesta el estado de la lista de parámetros (buscar,
// obtener, eliminar) sobre la capa api/. Sin JSX y sin HTTP directo.
//
// Nota de arquitectura: el patrón canónico AutoHub usa React Query. Este hook
// ya está migrado a React Query (ítem 15, fase 2): la lista se sirve vía
// useQuery keyeado por los filtros aplicados; `buscarParametros` fija esos
// filtros e invalida para forzar un re-fetch fresco. La interfaz pública
// (listaParametros/isLoading/buscado/buscarParametros/...) se mantiene idéntica,
// así la página consumidora no cambia.
// ============================================================================
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as parametrosApi from "../api/parametros.api";
import type { Parametro } from "../types/parametros.types";

export const useParametros = () => {
  const queryClient = useQueryClient();
  // Filtros efectivamente aplicados (null = aún sin buscar → no se dispara la query).
  const [filtros, setFiltros] = useState<any>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["seg-parametros", filtros],
    queryFn: async () => {
      const { parametros } = (await parametrosApi.parametrosBuscar(filtros)) || {};
      return parametros || [];
    },
    enabled: filtros !== null,
  });

  // Derivados (equivalentes 1:1 al estado local previo).
  const listaParametros: Parametro[] = data ?? [];
  const isLoading = isFetching;
  const buscado = filtros !== null;

  // Mantiene la firma previa (async, mismos filtros). Fija los filtros e invalida para
  // forzar re-fetch fresco aunque la clave no cambie (paridad con el flujo previo, que
  // siempre re-consultaba). El valor de retorno anterior no lo consumía ningún caller.
  const buscarParametros = async (nuevosFiltros) => {
    setFiltros(nuevosFiltros);
    queryClient.invalidateQueries({ queryKey: ["seg-parametros"] });
  };

  const obtenerParametro = async (dataPOST) => {
    const { parametro } = (await parametrosApi.parametroMostrar(dataPOST)) || {};
    return parametro;
  };

  const eliminarParametro = async (dataPOST) => {
    return parametrosApi.parametroDelete(dataPOST);
  };

  return {
    listaParametros,
    isLoading,
    buscado,
    buscarParametros,
    obtenerParametro,
    eliminarParametro,
  };
};

export default useParametros;
